// Apolo IDE — client do squad_call_log (Squad Orchestrator E2).
//
// Append/list das chamadas LLM do orquestrador via /api/hermes/runs/:id/calls
// (apiFetch auto-JWT). O HASH (sha-256, WebCrypto) é computado AQUI, na infra,
// a partir do material normalizado pelo domain (promptHashInput) — e o prompt
// CRU (system/user) NUNCA cruza o wire: só o `prompt_hash` vai pro D1 [SEC].
// Tudo best-effort: observabilidade jamais quebra o team turn.

import { apiFetch } from '../../lib/api.js';
import { promptHashInput, estimateContextTokens } from '../domain/squadCall.js';

async function sha256Hex(str) {
  try {
    const data = new TextEncoder().encode(String(str));
    const buf = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

export function makeSquadCallLogClient({ fetchImpl = apiFetch, hash = sha256Hex } = {}) {
  return {
    /**
     * Append one orchestrator LLM call. `entry` = { stage, model, temperature?,
     * top_p?, system?, user?, contextChars?, contextTokens? }. The raw
     * system/user are hashed locally and DROPPED before the request body.
     */
    async append(runId, entry) {
      if (!runId || !entry || !entry.stage) return;
      const promptHash =
        (await hash(promptHashInput({ stage: entry.stage, system: entry.system, user: entry.user }))) ||
        'unhashed';
      const body = {
        stage: entry.stage,
        model_id: entry.model || 'unknown',
        prompt_hash: promptHash,
        context_tokens:
          typeof entry.contextTokens === 'number'
            ? entry.contextTokens
            : estimateContextTokens(entry.contextChars),
      };
      if (typeof entry.temperature === 'number') body.temperature = entry.temperature;
      if (typeof entry.top_p === 'number') body.top_p = entry.top_p;
      // NB: entry.system / entry.user are NEVER included in `body`.
      try {
        await fetchImpl(`/api/hermes/runs/${runId}/calls`, { method: 'POST', body });
      } catch {
        /* best-effort — nunca quebra o turno */
      }
    },

    /** List a run's calls (oldest-first) for the observability drawer (E5). */
    async list(runId) {
      if (!runId) return [];
      try {
        const res = await fetchImpl(`/api/hermes/runs/${runId}/calls`, { method: 'GET' });
        return res?.ok ? res.data?.calls || [] : [];
      } catch {
        return [];
      }
    },
  };
}

export const squadCallLog = makeSquadCallLogClient();
